// Full-screen login gate + header account widget. The game is hidden behind the gate
// until the user signs in or chooses "continue as guest". Styling lives in index.html.
import { currentUsername, getIdToken, isConfigured, signIn, signOut, signUp } from './account';

export interface GateCallbacks {
  onSignedIn: (token: string, username: string) => void | Promise<void>;
  onSignedOut: () => void;
  onGuest: () => void;
}

export function mountLoginGate(cb: GateCallbacks): void {
  const gate = document.getElementById('login-gate')!;
  const accountArea = document.getElementById('account-area')!;
  if (!gate || !accountArea) return;

  const hide = (): void => {
    gate.style.display = 'none';
  };
  const show = (): void => {
    gate.replaceChildren(buildCard());
    gate.style.display = 'flex';
  };

  function renderAccount(username: string | null): void {
    accountArea.replaceChildren();
    if (username) {
      const who = el('span', `Signed in — ${username}`);
      const out = btn('Sign out', 'btn-link');
      out.onclick = () => {
        signOut();
        cb.onSignedOut();
        renderAccount(null);
        show();
      };
      accountArea.append(who, out);
    } else {
      accountArea.append(el('span', 'Guest'));
    }
  }

  function buildCard(): HTMLElement {
    const card = div('gate-card');
    card.append(el('div', 'WELCOME TO', 'gate-eyebrow'));
    card.append(el('div', 'Loki', 'gate-title'));
    card.append(el('div', 'Sign in to save your progress across devices.', 'gate-sub'));

    const form = div('gate-form');
    const user = input('username', 'text');
    const pass = input('password', 'password');
    const actions = div('gate-actions');
    const inBtn = btn('Sign In', 'btn btn-primary');
    const upBtn = btn('Create account', 'btn btn-text');
    actions.append(inBtn, upBtn);
    const status = div('gate-status');
    const guestWrap = div('gate-guest');
    const guest = btn('Continue as guest', 'btn-link');
    guestWrap.append(guest);
    form.append(user, pass, actions, status, guestWrap);
    card.append(form);

    const setStatus = (msg: string, error = false): void => {
      status.textContent = msg;
      status.className = `gate-status${error ? ' error' : ''}`;
    };

    const doSignIn = async (): Promise<void> => {
      try {
        setStatus('Signing in…');
        const name = user.value.trim();
        const token = await signIn(name, pass.value);
        renderAccount(name);
        hide();
        await cb.onSignedIn(token, name);
      } catch (e) {
        setStatus((e as Error).message ?? String(e), true);
      }
    };

    inBtn.onclick = doSignIn;
    upBtn.onclick = async () => {
      try {
        setStatus('Creating account…');
        await signUp(user.value.trim(), pass.value);
        await doSignIn();
      } catch (e) {
        setStatus((e as Error).message ?? String(e), true);
      }
    };
    guest.onclick = () => {
      hide();
      renderAccount(null);
      cb.onGuest();
    };
    pass.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void doSignIn();
    });
    return card;
  }

  // No backend configured → no gate; play immediately as guest.
  if (!isConfigured()) {
    hide();
    renderAccount(null);
    cb.onGuest();
    return;
  }

  // Resume an existing session if present, else show the gate.
  const existing = currentUsername();
  if (existing) {
    renderAccount(existing);
    hide();
    void getIdToken().then((t) => {
      if (t) void cb.onSignedIn(t, existing);
      else {
        renderAccount(null);
        show();
      }
    });
  } else {
    show();
  }
}

function div(className: string): HTMLDivElement {
  const d = document.createElement('div');
  d.className = className;
  return d;
}
function el(tag: string, text: string, className = ''): HTMLElement {
  const e = document.createElement(tag);
  e.textContent = text;
  if (className) e.className = className;
  return e;
}
function input(placeholder: string, type: string): HTMLInputElement {
  const i = document.createElement('input');
  i.placeholder = placeholder;
  i.type = type;
  return i;
}
function btn(label: string, className: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  b.className = className;
  return b;
}
